import { ChevronLeft, ChevronRight, UserCheck, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { DateField } from '../../../../components/inputs/DateField';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { TextField } from '../../../../components/inputs/TextField';
import { Toggle } from '../../../../components/inputs/Toggle';
import { Modal } from '../../../../components/Modal';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileNav, type ProfileNavEntry } from '../../../../components/profile/ProfileNav';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { findNavLabel } from '../../../../components/profile/profileSectionTitle';
import { ProfileShell } from '../../../../components/profile/ProfileShell';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText, type StatusTone } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { Tooltip } from '../../../../components/Tooltip';
import { ApiError } from '../../../../lib/apiError';
import { formatClockTime, formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { saveBlob } from '../../../../lib/saveBlob';
import { focusValidationError } from '../../../../lib/validationFocus';
import {
  APPLICANT_ASSESSMENT_STAGES,
  APPLICANT_HEALTH_PERMISSION,
  APPLICANT_QUALIFICATION,
  APPLICANT_STATUS,
  type ApplicantAssessment,
  type ApplicantAssessmentAttachment,
  type ApplicantListItem,
  type ApplicantScoreWeights,
  type JobPostingListItem,
} from '../../applicants/applicantTypes';
import { deliberationApi } from '../deliberationApi';
import {
  DELIBERATION_ENDORSE_PERMISSION,
  DELIBERATION_NOTE_MAX_LENGTH,
  DELIBERATION_UPDATE_PERMISSION,
  OATH_TAKING_PLACE_MAX_LENGTH,
  type ApplicantDeliberation,
  type DeliberationApplicantRow,
  type DeliberationStageScore,
  type DeliberationUnitOption,
} from '../deliberationTypes';
import { formatPoints } from '../formatPoints';
import { stageOutcome } from '../stageOutcome';
import { useRecruitmentUsers } from '../../vacancies/useRecruitmentUsers';
import type { RecruitmentUserOption } from '../../vacancies/vacancyTypes';
import { useEndorsementForm } from '../useEndorsementForm';
import { sampleAssignment, sampleDeliberation, sampleEndorsement, sampleOathTaking } from '../utils/sampleDeliberation';
import { EndorsementFields } from './EndorsementFields';
import { FileDownloadLink } from './FileDownloadLink';

type StageKey = (typeof APPLICANT_ASSESSMENT_STAGES)[number]['key'];

type PanelKey = 'info' | 'initial-interview' | 'summary' | 'decision' | 'endorsement' | 'appointment' | StageKey;

const ID = 'deliberation-applicant';

/**
 * The deliberation form's id prefix — each field's id is this plus the server's field name in
 * kebab case, so a rejected write focuses the field it names (`is-shortlisted`, `note`).
 */
const DELIBERATION_FORM_ID = `${ID}-deliberation`;

/** The oath-taking form's id prefix — `date`, `time`, `place`, the server's own field names. */
const OATH_FORM_ID = `${ID}-oath-taking`;

/** The assignment form's id prefix — `org-unit-id`, `effective-date`, `remarks`, the server's own field names. */
const ASSIGNMENT_FORM_ID = `${ID}-assignment`;

/** The endorsement form's id prefix — shared field ids, `endorsed-by-user-id`, `endorsed-date`, `remarks`. */
const ENDORSEMENT_FORM_ID = `${ID}-endorsement`;

/** No accounts — the loader handed to the picker for a reader who may not endorse. */
const NO_ACCOUNTS = async (): Promise<RecruitmentUserOption[]> => [];

/**
 * The rail (product calls, 20 Oct 2026): **Info**; then **Assessments**, an indented group running
 * from the Initial Interview through the five stages to the Summary; then **Decision**, Deliberate's
 * own record. **Initial Screening is not here** — the deliberation does not re-read it; it stays on
 * Applicants. No icons. A HEALTH stage carries `applicants.read-medical` — gating HIDES the entry, and
 * the server leaves its values out without the key.
 *
 * **Each STAGE states its outcome at the outer right** — `P` / `F` / `-` off the row's own stages. The
 * group starts OPEN, so landing on the Summary never lands inside a closed group.
 */
function navEntries(row: DeliberationApplicantRow): ProfileNavEntry[] {
  return [
    { key: 'info', label: 'Info' },
    {
      key: 'assessments',
      label: 'Assessments',
      defaultOpen: true,
      items: [
        { key: 'initial-interview', label: 'Initial Interview' },
        ...APPLICANT_ASSESSMENT_STAGES.map((s) => ({
          key: s.key,
          label: s.label,
          permission: s.isHealth ? APPLICANT_HEALTH_PERMISSION : undefined,
          badge: stageOutcome(row.stages.find((x) => x.stage === s.value)),
        })),
        { key: 'summary', label: 'Summary' },
      ],
    },
    { key: 'decision', label: 'Decision' },
    { key: 'endorsement', label: 'Endorsement' },
    { key: 'appointment', label: 'Appointment' },
  ];
}

/** One Summary line: the stage, its outcome, its weight and the points it earned. */
interface SummaryLine {
  label: string;
  stage: DeliberationStageScore | undefined;
  weight: number | null;
}

/**
 * One applicant's DELIBERATION PROFILE, in a full-screen modal over the posting's applicants — the
 * **sidebar** layout with the **header strip** above both columns. **Back** and **Next** step through
 * the applicants on the list's current page without closing (product call, 20 Oct 2026); the open
 * panel stays where it is, so one stage can be read across every applicant.
 *
 * **The whole record, and ONE write.** Info and every assessment card are Applicants' records, so none
 * carries an Edit. **Decision** is Deliberate's own: the Shortlist switch (in a banner saying what it
 * means) and, for a shortlisted applicant, the Note. With nothing saved it IS the form; once saved it is
 * the Deliberation card, whose cog holds its Edit — the one cog in the modal. The footer's tick, Submit
 * and Autogenerate show only on the Decision panel while it is a form. The RANK is automatic, by the
 * total, and lives in the applicants list. Nothing here can open a second modal.
 *
 * **The scores are the list row's**, scored by the server with the Summary's rule, so the row and this
 * profile cannot disagree.
 */
export function DeliberationApplicantModal({
  row,
  name,
  posting,
  weights,
  onPrevious,
  previousSurname,
  onNext,
  nextSurname,
  onClose,
  onSaved,
}: {
  row: DeliberationApplicantRow;
  /** Resolved by the panel through the deliberation names door. */
  name: string;
  posting: Pick<JobPostingListItem, 'id' | 'positionTitle' | 'itemNumber' | 'hasEndorsement' | 'orgUnitId'>;
  /** Score Points, for the Weight column. Null while loading or unreadable. */
  weights: ApplicantScoreWeights | null;
  /** The applicant above this one on the page — null on the first. */
  onPrevious: (() => void) | null;
  /** The previous applicant's surname, for the button's label — null until resolved. */
  previousSurname: string | null;
  /** The applicant below — null on the last. */
  onNext: (() => void) | null;
  /** The next applicant's surname — null until resolved. */
  nextSurname: string | null;
  onClose: () => void;
  /** The deliberation saved — the list refetches its badges and the row's rank and shortlist. */
  onSaved: () => void;
}) {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const { applicant } = row;

  // LANDS on the Summary (product call, 20 Oct 2026): a deliberation is opened to weigh the scores.
  const [panel, setPanel] = useState<PanelKey>('summary');
  const entries = navEntries(row);

  const [assessments, setAssessments] = useState<ApplicantAssessment[] | null>(null);
  const [assessmentsFailed, setAssessmentsFailed] = useState(false);

  /** The deliberation as stored — null while loading. */
  const [deliberation, setDeliberation] = useState<ApplicantDeliberation | null>(null);
  const [deliberationFailed, setDeliberationFailed] = useState(false);

  /**
   * A SAVED decision reopened as a form by its Edit. Nothing saved yet needs no Edit: this is a modal,
   * and it submits — the form is simply OPEN (product call, 20 Oct 2026: "edit only applies if there is
   * already something saved").
   */
  const [editing, setEditing] = useState(false);
  const [shortlisted, setShortlisted] = useState(false);
  const [note, setNote] = useState('');
  /** Bumped on every reseed: the editor is uncontrolled after mount, so a new document REMOUNTS it. */
  const [editorKey, setEditorKey] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);

  const mayWrite = hasPermission(DELIBERATION_UPDATE_PERMISSION);
  /** Something is on record — only then is there anything to Edit. */
  const isSaved = deliberation?.updatedAt != null;
  /** The Decision is a FORM: nothing saved yet, or a saved one reopened by Edit. For a writer only. */
  const formOpen = mayWrite && deliberation !== null && (!isSaved || editing);
  /** A NEW decision with something typed — stepping away would lose it. */
  const dirty = !isSaved && (shortlisted || note.trim() !== '');

  /*
    THE SCHEDULE OF OATH TAKING — the Appointment panel's form (product call, 20 Oct 2026), for an
    ENDORSED applicant under `deliberations.endorse`. The Decision's shape: OPEN while nothing is
    scheduled, a table with its Edit in the card's cog once it is.
  */
  const [oathEditing, setOathEditing] = useState(false);
  const [oathDate, setOathDate] = useState('');
  const [oathTime, setOathTime] = useState('');
  const [oathPlace, setOathPlace] = useState('');
  /** The oath taking's NOTES (21 Oct 2026) — rich text, optional; the editor REMOUNTS on every reseed. */
  const [oathNotes, setOathNotes] = useState('');
  const [oathEditorKey, setOathEditorKey] = useState(0);

  const mayEndorse = hasPermission(DELIBERATION_ENDORSE_PERMISSION);
  const isEndorsed = deliberation?.isEndorsed === true;
  const oathSaved = deliberation?.oathTakingDate != null;
  const oathFormOpen = mayEndorse && isEndorsed && (!oathSaved || oathEditing);
  const oathValid = oathDate !== '' && oathTime !== '' && oathPlace.trim() !== '';
  const oathDirty = !oathSaved && (oathDate !== '' || oathTime !== '' || oathPlace.trim() !== '');

  /*
    THE ASSIGNMENT — its own panel (product call, 21 Oct 2026): the unit the appointee is to be assigned
    to, from when, and a remark. PLANNED, on the deliberation — the applicant has no 201 file yet, so
    Personnel still records the service record. The Oath Taking's shape: an ENDORSED applicant under
    `deliberations.endorse`, OPEN while nothing is recorded, a table with its Edit in the cog once it is.
  */
  const [assignmentEditing, setAssignmentEditing] = useState(false);
  const [assignmentUnitId, setAssignmentUnitId] = useState('');
  const [assignmentDate, setAssignmentDate] = useState('');
  const [assignmentRemarks, setAssignmentRemarks] = useState('');
  /** Null until loaded — and never loaded for a reader who may not endorse. */
  const [units, setUnits] = useState<DeliberationUnitOption[] | null>(null);
  const unitsLoading = mayEndorse && units === null;
  const assignmentSaved = deliberation?.assignmentOrgUnitId != null;
  const assignmentFormOpen = mayEndorse && isEndorsed && (!assignmentSaved || assignmentEditing);
  const assignmentValid = assignmentUnitId !== '' && assignmentDate !== '';
  const assignmentDirty = !assignmentSaved && (assignmentDate !== '' || assignmentRemarks.trim() !== '');

  /*
    The APPOINTMENT panel's ONE Submit (product call, 22 Oct 2026) saves each OPEN section somebody filled
    in — a blank one is left alone, a half-filled one holds the Submit back. The Unit is seeded, so it alone
    does not make the Assignment "filled in".
  */
  const oathBlank =
    oathDate === '' && oathTime === '' && oathPlace.trim() === '' && oathNotes.replace(/<[^>]*>/g, '').trim() === '';
  const assignmentBlank = assignmentDate === '' && assignmentRemarks.trim() === '';
  const oathToSave = oathFormOpen && (oathEditing || !oathBlank);
  const assignmentToSave = assignmentFormOpen && (assignmentEditing || !assignmentBlank);
  const appointmentSubmittable =
    (oathToSave || assignmentToSave) && (!oathToSave || oathValid) && (!assignmentToSave || assignmentValid);

  /*
    THE ENDORSEMENT — its own panel (product call, 21 Oct 2026): who, when, remarks, one document. The
    Decision's shape again: OPEN for a SHORTLISTED applicant not yet endorsed, a table with its Edit in
    the card's cog once endorsed. One hook and one field set with the row kebab's popup.
  */
  const endorsement = useEndorsementForm({ postingId: posting.id, applicantId: applicant.id });
  // The accounts load only for someone who may endorse — anyone else would only meet a refusal.
  const accounts = useRecruitmentUsers(mayEndorse ? deliberationApi.userOptions : NO_ACCOUNTS);
  const [endorsementEditing, setEndorsementEditing] = useState(false);
  const isShortlistedOnRecord = deliberation?.isShortlisted === true;
  // ONE endorsement per posting — with another made, the form opens only for this applicant's own.
  const endorsementFormOpen =
    mayEndorse && isShortlistedOnRecord && (isEndorsed ? endorsementEditing : !posting.hasEndorsement);
  /** A NEW endorsement with something typed beyond its defaults — stepping away would lose it. */
  const endorsementDirty =
    !isEndorsed && endorsementFormOpen && (endorsement.remarks.trim() !== '' || endorsement.files.length > 0);

  /**
   * Which form the FOOTER serves — its tick, Submit and Autogenerate show only on that form's own panel
   * (product call, 20 Oct 2026). Null on every other panel, which shows Close alone.
   */
  const footerForm: 'decision' | 'endorsement' | 'appointment' | null =
    panel === 'decision' && formOpen
      ? 'decision'
      : panel === 'endorsement' && endorsementFormOpen
        ? 'endorsement'
        : panel === 'appointment' && (oathFormOpen || assignmentFormOpen)
          ? 'appointment'
          : null;
  const footerEditing =
    footerForm === 'decision'
      ? editing
      : footerForm === 'endorsement'
        ? endorsementEditing
        : footerForm === 'appointment'
          ? oathEditing || assignmentEditing
          : false;
  const footerSaving = saving || endorsement.saving;

  /*
    A different applicant (Back / Next) drops what was on screen FIRST — adjusted during RENDER, not in
    the effect: React re-renders before committing, so the previous applicant's stages and decision are
    never painted under the new name. The effect below only loads.
  */
  const [shownFor, setShownFor] = useState(applicant.id);

  if (shownFor !== applicant.id) {
    setShownFor(applicant.id);
    setAssessments(null);
    setAssessmentsFailed(false);
    setDeliberation(null);
    setDeliberationFailed(false);
    setEditing(false);
    setShortlisted(false);
    setNote('');
    setConfirmed(false);
    setSaveError(null);
    setOathEditing(false);
    setOathDate('');
    setOathTime('');
    setOathPlace('');
    setOathNotes('');
    setAssignmentEditing(false);
    setAssignmentUnitId('');
    setAssignmentDate('');
    setAssignmentRemarks('');
    setEndorsementEditing(false);
    endorsement.seed(null);
  }

  useEffect(() => {
    const controller = new AbortController();

    deliberationApi
      .assessments(applicant.id, controller.signal)
      .then(setAssessments)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setAssessmentsFailed(true);
      });

    deliberationApi
      .deliberation(applicant.id, controller.signal)
      .then((loaded) => {
        setDeliberation(loaded);
        // A fresh editor for this applicant — blank when nothing is saved, the form being open.
        setEditorKey((k) => k + 1);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setDeliberationFailed(true);
      });

    return () => controller.abort();
  }, [applicant.id]);

  // The units load once, and only for someone who may record an assignment — anyone else meets a refusal.
  useEffect(() => {
    if (!mayEndorse) return;

    const controller = new AbortController();

    deliberationApi
      .unitOptions(controller.signal)
      .then(setUnits)
      // A failed feed leaves the picker empty rather than spinning.
      .catch(() => setUnits([]));

    return () => controller.abort();
  }, [mayEndorse]);

  /*
    A NEW assignment opens on the posting's own office (product call, 21 Oct 2026) — seeded once the
    record arrives, adjusted during render rather than in an effect; the reader may pick another.
  */
  const [assignmentSeededFor, setAssignmentSeededFor] = useState<string | null>(null);

  if (deliberation !== null && assignmentSeededFor !== applicant.id) {
    setAssignmentSeededFor(applicant.id);
    if (deliberation.assignmentOrgUnitId == null) setAssignmentUnitId(posting.orgUnitId);
  }

  /** Reopens a SAVED decision as a form, seeded from what is stored — never from blank. */
  function startEditing() {
    if (!deliberation) return;

    setShortlisted(deliberation.isShortlisted);
    setNote(deliberation.note ?? '');
    setEditorKey((k) => k + 1);
    setConfirmed(false);
    setSaveError(null);
    setEditing(true);
    setPanel('decision');
  }

  async function save() {
    if (!formOpen || !confirmed || saving) return;

    setSaving(true);
    setSaveError(null);

    try {
      const saved = await deliberationApi.saveDeliberation(posting.id, applicant.id, {
        isShortlisted: shortlisted,
        note: !shortlisted || note.trim() === '' ? null : note,
      });

      setDeliberation(saved);
      setEditing(false);
      setConfirmed(false);
      toast.success(saved.isShortlisted ? `${name} is shortlisted as a candidate.` : 'Decision saved.');
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the decision.', 0);
      setSaveError(failure);
      setPanel('decision');
      // Focus once the panel has drawn.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => focusValidationError(failure, { idPrefix: DELIBERATION_FORM_ID })),
      );
    } finally {
      setSaving(false);
    }
  }

  /** Reopens a SCHEDULED oath taking as a form, seeded from what is stored. */
  function startOathEditing() {
    if (!deliberation) return;

    setOathDate(deliberation.oathTakingDate ?? '');
    setOathTime(deliberation.oathTakingTime ? deliberation.oathTakingTime.slice(0, 5) : '');
    setOathPlace(deliberation.oathTakingPlace ?? '');
    setOathNotes(deliberation.oathTakingNotes ?? '');
    setOathEditorKey((k) => k + 1);
    setConfirmed(false);
    setSaveError(null);
    setOathEditing(true);
    setPanel('appointment');
  }

  /** Reopens a RECORDED assignment as a form, seeded from what is stored. */
  function startAssignmentEditing() {
    if (!deliberation) return;

    setAssignmentUnitId(deliberation.assignmentOrgUnitId ?? '');
    setAssignmentDate(deliberation.assignmentEffectiveDate ?? '');
    setAssignmentRemarks(deliberation.assignmentRemarks ?? '');
    setConfirmed(false);
    setSaveError(null);
    setAssignmentEditing(true);
    setPanel('appointment');
  }

  /** Reopens a MADE endorsement as the form, seeded from what is stored. */
  function startEndorsementEditing() {
    if (!deliberation) return;

    endorsement.seed(deliberation);
    setConfirmed(false);
    setEndorsementEditing(true);
    setPanel('endorsement');
  }

  async function saveEndorsement() {
    if (!endorsementFormOpen || !confirmed) return;

    const result = await endorsement.submit();

    if (!result) return;

    if ('failure' in result) {
      const { failure } = result;
      requestAnimationFrame(() =>
        requestAnimationFrame(() => focusValidationError(failure, { idPrefix: ENDORSEMENT_FORM_ID })),
      );
      return;
    }

    setDeliberation(result.saved);
    setEndorsementEditing(false);
    setConfirmed(false);
    if (result.fileFailed) {
      toast.failure(`Endorsed, but the document was not attached: ${result.fileFailed}.`);
    } else {
      toast.success(`${name} is endorsed for appointment.`);
    }
    onSaved();
  }

  /** The Appointment panel's Submit — the oath taking, then the assignment, each only if filled in. */
  async function saveAppointment() {
    if (footerForm !== 'appointment' || !confirmed || !appointmentSubmittable || saving) return;

    setSaving(true);
    setSaveError(null);
    // Which section a refusal belongs to — the focus goes to its field.
    let failing = OATH_FORM_ID;

    try {
      if (oathToSave) {
        const saved = await deliberationApi.saveOathTaking(posting.id, applicant.id, {
          date: oathDate,
          time: `${oathTime}:00`,
          place: oathPlace.trim(),
          notes: oathNotes.trim() === '' ? null : oathNotes,
        });
        setDeliberation(saved);
        setOathEditing(false);
      }

      if (assignmentToSave) {
        failing = ASSIGNMENT_FORM_ID;
        const saved = await deliberationApi.saveAssignment(posting.id, applicant.id, {
          orgUnitId: assignmentUnitId,
          effectiveDate: assignmentDate,
          remarks: assignmentRemarks.trim() === '' ? null : assignmentRemarks.trim(),
        });
        setDeliberation(saved);
        setAssignmentEditing(false);
      }

      setConfirmed(false);
      toast.success(
        oathToSave && assignmentToSave
          ? 'Appointment recorded.'
          : oathToSave
            ? 'Oath taking scheduled.'
            : 'Assignment recorded.',
      );
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the appointment.', 0);
      setSaveError(failure);
      setPanel('appointment');
      requestAnimationFrame(() => requestAnimationFrame(() => focusValidationError(failure, { idPrefix: failing })));
    } finally {
      setSaving(false);
    }
  }

  /** Served as an attachment; saved through an object URL, never opened inline. */
  async function download(fetchFile: () => Promise<{ blob: Blob; fileName: string | null }>, fallbackName: string) {
    try {
      const { blob, fileName } = await fetchFile();
      saveBlob(blob, fileName ?? fallbackName);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not download the file.');
    }
  }

  const stage = APPLICANT_ASSESSMENT_STAGES.find((s) => s.key === panel);

  // Moving on would drop what was typed, so it is finished first.
  const stepBlocked =
    editing || oathEditing || endorsementEditing || assignmentEditing
      ? 'Submit or cancel the edit first.'
      : dirty
        ? 'Submit the decision first — or clear what was entered.'
        : endorsementDirty
          ? 'Submit the endorsement first — or clear what was entered.'
          : oathDirty
            ? 'Submit the oath taking first — or clear what was entered.'
            : assignmentDirty
              ? 'Submit the assignment first — or clear what was entered.'
              : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Applicant deliberation"
      size="fullscreen"
      // The profile shell owns its scroll regions — the nav and the panel each scroll on their own.
      bodyScroll={false}
      id={`${ID}-modal`}
      // The tick and Autogenerate belong to the FORM on screen — the Decision's or the Oath Taking's.
      autogenerate={
        footerForm
          ? {
              onGenerate: () => {
                if (footerForm === 'decision') {
                  setNote(sampleDeliberation().note);
                  setEditorKey((k) => k + 1);
                } else if (footerForm === 'endorsement') {
                  const sample = sampleEndorsement(accounts.users);
                  if (sample.endorsedByUserId) endorsement.setEndorsedBy(sample.endorsedByUserId);
                  endorsement.setEndorsedDate(sample.endorsedDate);
                  endorsement.setRemarks(sample.remarks);
                } else {
                  // The Appointment panel — every OPEN section.
                  if (oathFormOpen) {
                    const sample = sampleOathTaking();
                    setOathDate(sample.date);
                    setOathTime(sample.time);
                    setOathPlace(sample.place);
                    setOathNotes(sample.notes);
                    setOathEditorKey((k) => k + 1);
                  }
                  if (assignmentFormOpen) {
                    const sample = sampleAssignment(units ?? [], posting.orgUnitId);
                    if (sample.orgUnitId) setAssignmentUnitId(sample.orgUnitId);
                    setAssignmentDate(sample.effectiveDate);
                    setAssignmentRemarks(sample.remarks);
                  }
                }
              },
              disabled: footerSaving,
            }
          : undefined
      }
      confirm={
        footerForm
          ? { id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: footerSaving }
          : undefined
      }
      // On the LEFT, before Autogenerate (product call, 20 Oct 2026), named by the neighbour's surname.
      footerLeading={
        <>
          <StepButton
            id={`${ID}-previous`}
            direction="previous"
            surname={previousSurname}
            onClick={onPrevious}
            disabledReason={stepBlocked ?? (onPrevious ? null : 'This is the first applicant on this page.')}
          />
          <StepButton
            id={`${ID}-next`}
            direction="next"
            surname={nextSurname}
            onClick={onNext}
            disabledReason={stepBlocked ?? (onNext ? null : 'This is the last applicant on this page.')}
          />
        </>
      }
      footer={
        <>
          {footerForm ? (
            <>
              {/* Cancel leaves a reopened SAVED record; a new one has nothing to go back to, so Close. */}
              {footerEditing ? (
                <Button
                  id={`${ID}-cancel-edit`}
                  variant="secondary"
                  onClick={() =>
                    footerForm === 'decision'
                      ? setEditing(false)
                      : footerForm === 'endorsement'
                        ? setEndorsementEditing(false)
                        : (setOathEditing(false), setAssignmentEditing(false))
                  }
                  disabled={footerSaving}
                >
                  Cancel
                </Button>
              ) : (
                <Button id={`${ID}-close`} variant="secondary" onClick={onClose} disabled={footerSaving}>
                  Close
                </Button>
              )}
              <Button
                id={`${ID}-submit`}
                variant="primary"
                onClick={() =>
                  void (footerForm === 'decision'
                    ? save()
                    : footerForm === 'endorsement'
                      ? saveEndorsement()
                      : saveAppointment())
                }
                disabled={
                  !confirmed ||
                  footerSaving ||
                  (footerForm === 'appointment' && !appointmentSubmittable) ||
                  (footerForm === 'endorsement' && !endorsement.valid)
                }
                loading={footerSaving}
                loadingLabel="Submitting"
              >
                Submit
              </Button>
            </>
          ) : (
            <Button id={`${ID}-close`} variant="secondary" onClick={onClose}>
              Close
            </Button>
          )}
        </>
      }
    >
      <ProfileShell
        id={`${ID}-profile`}
        // The heading every panel states — resolved from the nav, so the two cannot disagree.
        sectionTitle={findNavLabel(entries, panel)}
        header={
          <ProfileHeader
            id={`${ID}-profile-header`}
            icon={<UserRound className="h-5 w-5" />}
            title={name}
            meta={[
              posting.itemNumber,
              <span key="post" className="font-sans">
                {posting.positionTitle}
              </span>,
            ]}
            status={<StatusText tone={stageTone(applicant)}>{applicant.statusLabel}</StatusText>}
            createdBy={applicant.createdByName}
            createdAt={applicant.createdAt}
          />
        }
        banner={<ProfileBanner id={`${ID}-profile-banner`} tone={bannerTone(applicant)} message={bannerMessage(row)} />}
        sidebar={
          <ProfileNav
            id={`${ID}-nav`}
            entries={entries}
            activeKey={panel}
            onSelect={(key) => setPanel(key as PanelKey)}
            hasPermission={hasPermission}
          />
        }
      >
        {/*
          NO PANEL HEADING, on ANY panel (product call, 20 Oct 2026, stated more than once: "no outside
          label for panels") — the cards' own titles name what is there. The empty title overrides the
          one the nav would supply.
        */}
        <ProfileSection id={`${ID}-section`} title="">
          {panel === 'info' ? (
            <>
              <ProfileCard id={`${ID}-profile-applicant`} title="Applicant">
                <PreviewTable variant="table-display" rows={applicantRows(applicant, name)} />
              </ProfileCard>

              {/* Never editable: a record of what happened. */}
              <ProfileCard id={`${ID}-profile-audit`} title="Audit">
                <PreviewTable variant="table-display" rows={auditRows(applicant)} />
              </ProfileCard>
            </>
          ) : panel === 'initial-interview' ? (
            <ProfileCard
              id={`${ID}-profile-interview`}
              title="Initial Interview"
              empty={
                applicant.interviewDate == null
                  ? {
                      title: 'No interview scheduled.',
                      description:
                        applicant.screeningResult === APPLICANT_QUALIFICATION.Qualified
                          ? undefined
                          : 'An interview is scheduled once the applicant is screened Qualified.',
                    }
                  : undefined
              }
            >
              <PreviewTable variant="table-display" rows={interviewRows(applicant)} />
            </ProfileCard>
          ) : panel === 'summary' ? (
            // A DERIVED view of the stages, which are recorded on Applicants — so no Edit.
            <SummaryCard row={row} weights={weights} />
          ) : panel === 'decision' ? (
            deliberationFailed ? (
              <ProfileCard id={`${ID}-profile-decision`} title="Deliberation" padded>
                <p className="text-sm text-slate-500">
                  The decision could not be loaded. Close and reopen the applicant to try again.
                </p>
              </ProfileCard>
            ) : deliberation === null ? (
              <Skeleton className="h-40 w-full" />
            ) : formOpen ? (
              <>
                {saveError ? (
                  <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-xs text-red-700">{saveError.message}</p>
                  </div>
                ) : null}

                {/*
                  The SWITCH sits in a banner that says what it means (product call, 20 Oct 2026). A switch
                  for a STORED value is a deliberate departure from `<Toggle>`'s own rule: it is seeded from
                  the record, so it always shows a real answer, and the request still sends a `bool?`.
                */}
                <NextActionAlert
                  id={`${ID}-shortlist-banner`}
                  tone="blue"
                  icon={<UserCheck className="h-5 w-5" aria-hidden="true" />}
                  title="Shortlist as a candidate"
                  description={`A shortlisted applicant becomes a candidate for ${posting.positionTitle}, and is listed under Short Listed on the posting's applicants.`}
                  action={
                    // LARGE and centred on the banner's height (product call, 20 Oct 2026) — the banner
                    // aligns its row to the top, so the switch's own slot centres itself.
                    <span className="flex shrink-0 self-center">
                      <Toggle
                        id={`${DELIBERATION_FORM_ID}-is-shortlisted`}
                        label="Shortlisted"
                        size="lg"
                        checked={shortlisted}
                        onChange={setShortlisted}
                        disabled={saving}
                      />
                    </span>
                  }
                />

                {/*
                  The Deliberation card OPENS ONLY FOR A SHORTLISTED applicant (product call, 20 Oct 2026:
                  "there's no point" otherwise). Switching it off hides the note, and the save CLEARS it — the
                  server refuses to keep one on an applicant not shortlisted. The RANK is not here: it is
                  automatic, by the total (product call, 20 Oct 2026), and shows in the applicants list.
                */}
                {shortlisted ? (
                  <ProfileCard id={`${ID}-profile-decision`} title="Deliberation" padded>
                    <div className="flex flex-col gap-4">
                      <LazyRichTextField
                        key={editorKey}
                        id={`${DELIBERATION_FORM_ID}-note`}
                        label="Note"
                        description="The deliberation's note on this applicant. Formatting, lists and tables are kept; anything the toolbar cannot produce is removed when it is saved."
                        value={note}
                        onChange={setNote}
                        maxLength={DELIBERATION_NOTE_MAX_LENGTH}
                        error={saveError?.fieldError('note')}
                        disabled={saving}
                        placeholder="What the deliberation concluded…"
                      />
                    </div>
                  </ProfileCard>
                ) : null}
              </>
            ) : (
              /*
                Saved — the Deliberation CARD, its Edit the first entry of the card's own cog under
                `deliberations.update` (frontend-profiles.md: "a settings cog whose first entry is Edit").
                Edit exists only for something SAVED; a writer meeting nothing saved has the form open, and
                a reader meets the empty state. The Note is blank unless shortlisted — the save clears it.
              */
              <ProfileCard
                id={`${ID}-profile-decision`}
                title="Deliberation"
                onEdit={isSaved ? startEditing : undefined}
                editPermission={DELIBERATION_UPDATE_PERMISSION}
                empty={isSaved ? undefined : { title: 'No decision recorded yet.' }}
              >
                <PreviewTable variant="table-display" rows={deliberationRows(deliberation)} />
              </ProfileCard>
            )
          ) : panel === 'endorsement' ? (
            /*
              ENDORSEMENT (product call, 21 Oct 2026) — always in the rail, saying so until it applies. A
              SHORTLISTED applicant not yet endorsed meets the FORM (an endorser); once endorsed, the table,
              its Edit in the card's cog under `deliberations.endorse`. Withdrawing stays the row kebab's,
              behind its own confirmation — a dialog over this modal would stack two.
            */
            deliberationFailed ? (
              <ProfileCard id={`${ID}-profile-endorsement`} title="Endorsement" padded>
                <p className="text-sm text-slate-500">
                  The endorsement could not be loaded. Close and reopen the applicant to try again.
                </p>
              </ProfileCard>
            ) : deliberation === null ? (
              <Skeleton className="h-40 w-full" />
            ) : endorsementFormOpen ? (
              <ProfileCard id={`${ID}-profile-endorsement`} title="Endorsement" padded>
                <EndorsementFields
                  idPrefix={ENDORSEMENT_FORM_ID}
                  form={endorsement}
                  users={accounts.users}
                  usersLoading={accounts.loading}
                  usersError={accounts.error}
                  currentFileName={deliberation.endorsementFileName}
                />
              </ProfileCard>
            ) : (
              <ProfileCard
                id={`${ID}-profile-endorsement`}
                title="Endorsement"
                onEdit={isEndorsed ? startEndorsementEditing : undefined}
                editPermission={DELIBERATION_ENDORSE_PERMISSION}
                empty={
                  isEndorsed
                    ? undefined
                    : isShortlistedOnRecord
                      ? posting.hasEndorsement
                        ? {
                            title: 'Not endorsed for appointment.',
                            description:
                              'Another applicant on this posting is endorsed for appointment. Withdraw that endorsement first.',
                          }
                        : { title: 'Not endorsed for appointment yet.' }
                      : {
                          title: 'Not shortlisted.',
                          description:
                            'Only a shortlisted applicant is endorsed for appointment — shortlist them in their Decision first.',
                        }
                }
              >
                <PreviewTable
                  variant="table-display"
                  rows={endorsementRows(
                    deliberation,
                    () =>
                      void download(
                        () => deliberationApi.downloadEndorsementFile(posting.id, applicant.id),
                        deliberation.endorsementFileName ?? 'endorsement',
                      ),
                  )}
                />
              </ProfileCard>
            )
          ) : panel === 'appointment' ? (
            /*
              APPOINTMENT (product calls, 20–22 Oct 2026) — TWO sections, the Schedule of Oath Taking and the
              Assignment, for an ENDORSED applicant, under ONE Submit that saves whichever section was filled in.
              Always in the rail (a panel never appears and vanishes on a flag), and it says so until the
              applicant is endorsed. Endorsed: a FORM while nothing is scheduled, then the card, its Edit in
              the cog under `deliberations.endorse`.
            */
            deliberationFailed ? (
              <ProfileCard id={`${ID}-profile-oath`} title="Schedule of Oath Taking" padded>
                <p className="text-sm text-slate-500">
                  The appointment could not be loaded. Close and reopen the applicant to try again.
                </p>
              </ProfileCard>
            ) : deliberation === null ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <>
                {/* ONE strip for the panel — a refusal from either section's save. */}
                {saveError && (oathFormOpen || assignmentFormOpen) ? (
                  <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-xs text-red-700">{saveError.message}</p>
                  </div>
                ) : null}

                {oathFormOpen ? (
                  <ProfileCard id={`${ID}-profile-oath`} title="Schedule of Oath Taking" padded>
                    <div className="flex flex-col gap-4">
                      <TextField
                        id={`${OATH_FORM_ID}-place`}
                        label="Place"
                        required
                        description="Where the oath taking is held — a room, a building, or an office."
                        value={oathPlace}
                        maxLength={OATH_TAKING_PLACE_MAX_LENGTH}
                        onChange={(e) => setOathPlace(e.target.value)}
                        error={
                          oathPlace.trim() ? saveError?.fieldError('place') : 'Enter where the oath taking is held.'
                        }
                        disabled={saving}
                      />

                      <div className="grid gap-4 sm:grid-cols-2">
                        <DateField
                          id={`${OATH_FORM_ID}-date`}
                          label="Date"
                          required
                          description="The day of the oath taking."
                          value={oathDate}
                          onChange={setOathDate}
                          error={oathDate ? saveError?.fieldError('date') : 'Enter the date of the oath taking.'}
                          disabled={saving}
                        />

                        <TextField
                          id={`${OATH_FORM_ID}-time`}
                          label="Time"
                          required
                          type="time"
                          value={oathTime}
                          onChange={(e) => setOathTime(e.target.value)}
                          error={oathTime ? saveError?.fieldError('time') : 'Enter the time of the oath taking.'}
                          disabled={saving}
                        />
                      </div>

                      <LazyRichTextField
                        key={oathEditorKey}
                        id={`${OATH_FORM_ID}-notes`}
                        label="Notes"
                        description="Anything the appointee should know about the oath taking. Formatting, lists and tables are kept; anything the toolbar cannot produce is removed when it is saved."
                        value={oathNotes}
                        onChange={setOathNotes}
                        maxLength={DELIBERATION_NOTE_MAX_LENGTH}
                        error={saveError?.fieldError('notes')}
                        disabled={saving}
                      />
                    </div>
                  </ProfileCard>
                ) : (
                  <ProfileCard
                    id={`${ID}-profile-oath`}
                    title="Schedule of Oath Taking"
                    onEdit={isEndorsed && oathSaved ? startOathEditing : undefined}
                    editPermission={DELIBERATION_ENDORSE_PERMISSION}
                    empty={
                      !isEndorsed
                        ? {
                            title: 'Not endorsed for appointment.',
                            description:
                              "An applicant is endorsed in their Endorsement, or from the applicants list's row menu; the oath taking is then scheduled here.",
                          }
                        : !oathSaved
                          ? { title: 'No oath taking scheduled yet.' }
                          : undefined
                    }
                  >
                    <PreviewTable variant="table-display" rows={oathRows(deliberation)} />
                  </ProfileCard>
                )}

                {/*
                  ASSIGNMENT (product calls, 21–22 Oct 2026) — the planned unit, the panel's SECOND section. A
                  FORM while nothing is recorded, then the card, its Edit in the cog under `deliberations.endorse`.
                */}
                {assignmentFormOpen ? (
                  <ProfileCard id={`${ID}-profile-assignment`} title="Assignment" padded>
                    <div className="flex flex-col gap-4">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <Select
                          id={`${ASSIGNMENT_FORM_ID}-org-unit-id`}
                          label="Unit"
                          required
                          description="The unit the appointee is to be assigned to — the posting's own office unless chosen otherwise. Planned here; Personnel records the service record."
                          placeholder="Choose a unit…"
                          value={assignmentUnitId}
                          onChange={(value) => setAssignmentUnitId(value)}
                          options={(units ?? []).map((u) => ({
                            value: u.id,
                            label: u.name,
                            sublabel: u.orgUnitTypeName,
                          }))}
                          loading={unitsLoading}
                          disabled={saving}
                          error={
                            assignmentUnitId
                              ? saveError?.fieldError('orgUnitId')
                              : 'Choose the unit the appointee is assigned to.'
                          }
                        />

                        <DateField
                          id={`${ASSIGNMENT_FORM_ID}-effective-date`}
                          label="Effective date"
                          required
                          description="The day the assignment takes effect."
                          value={assignmentDate}
                          onChange={setAssignmentDate}
                          error={
                            assignmentDate
                              ? saveError?.fieldError('effectiveDate')
                              : 'Enter the date the assignment takes effect.'
                          }
                          disabled={saving}
                        />
                      </div>

                      <Textarea
                        id={`${ASSIGNMENT_FORM_ID}-remarks`}
                        label="Remarks"
                        value={assignmentRemarks}
                        onChange={(e) => setAssignmentRemarks(e.target.value)}
                        error={saveError?.fieldError('remarks')}
                        disabled={saving}
                      />
                    </div>
                  </ProfileCard>
                ) : (
                  <ProfileCard
                    id={`${ID}-profile-assignment`}
                    title="Assignment"
                    onEdit={isEndorsed && assignmentSaved ? startAssignmentEditing : undefined}
                    editPermission={DELIBERATION_ENDORSE_PERMISSION}
                    empty={
                      !isEndorsed
                        ? {
                            title: 'Not endorsed for appointment.',
                            description:
                              "An applicant is endorsed in their Endorsement, or from the applicants list's row menu; the assignment is then recorded here.",
                          }
                        : !assignmentSaved
                          ? { title: 'No assignment recorded yet.' }
                          : undefined
                    }
                  >
                    <PreviewTable variant="table-display" rows={assignmentRows(deliberation)} />
                  </ProfileCard>
                )}
              </>
            )
          ) : stage ? (
            <StageCard
              label={stage.label}
              assessment={assessments?.find((a) => a.stage === stage.value) ?? null}
              failed={assessmentsFailed}
              onDownload={(a, file) =>
                void download(
                  () => deliberationApi.downloadAssessmentFile(applicant.id, a.stage, file.id),
                  file.fileName,
                )
              }
            />
          ) : null}
        </ProfileSection>
      </ProfileShell>
    </Modal>
  );
}

/**
 * The previous / next applicant, LABELLED BY THEIR SURNAME (product call, 20 Oct 2026) — `‹ Quedado`,
 * `Macatane ›` — with "Back to" / "Next to" spoken before it, so the accessible name says where it goes.
 * Plain Back / Next where there is no neighbour or no name yet.
 *
 * **In the tab run on purpose** (`tabIndex={0}`): a footer drops its secondary buttons from the run
 * because Esc already does what Cancel does — and nothing else steps to another applicant, so dropping
 * these would leave them pointer-only (WCAG 2.1.1). Disabled for state, saying why.
 */
function StepButton({
  id,
  direction,
  surname,
  onClick,
  disabledReason,
}: {
  id: string;
  direction: 'previous' | 'next';
  surname: string | null;
  onClick: (() => void) | null;
  disabledReason: string | null;
}) {
  const previous = direction === 'previous';
  const label = onClick && surname ? surname : previous ? 'Back' : 'Next';

  const button = (
    <Button
      id={id}
      variant="secondary"
      leading={previous ? <ChevronLeft className="h-4 w-4" aria-hidden="true" /> : undefined}
      onClick={() => onClick?.()}
      disabled={disabledReason !== null}
      tabIndex={0}
    >
      {/*
        One no-wrap row: `<Button>` lays out a LEADING icon only, so Next's chevron rides in the label —
        loose beside the text, it wrapped under it.
      */}
      <span className="inline-flex items-center gap-2 whitespace-nowrap">
        {onClick && surname ? <span className="sr-only">{previous ? 'Back to ' : 'Next to '}</span> : null}
        {label}
        {previous ? null : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
      </span>
    </Button>
  );

  return disabledReason ? <Tooltip label={disabledReason}>{button}</Tooltip> : button;
}

/**
 * One assessment stage — Result, Remarks (the stored document, through the one rich-text surface),
 * Attachments (each downloads), and who last recorded it. Read only, and no cog.
 */
function StageCard({
  label,
  assessment,
  failed,
  onDownload,
}: {
  label: string;
  /** Null while the stages load. */
  assessment: ApplicantAssessment | null;
  failed: boolean;
  onDownload: (assessment: ApplicantAssessment, file: ApplicantAssessmentAttachment) => void;
}) {
  if (failed) {
    return (
      <ProfileCard id={`${ID}-profile-stage`} title={label} padded>
        <p className="text-sm text-slate-500">
          This stage could not be loaded. Close and reopen the applicant to try again.
        </p>
      </ProfileCard>
    );
  }

  return (
    <ProfileCard
      id={`${ID}-profile-stage`}
      title={label}
      empty={assessment && assessment.result == null ? { title: 'Not recorded yet.' } : undefined}
    >
      {assessment ? (
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Result', value: assessment.resultLabel },
            {
              label: 'Remarks',
              value: assessment.remarks ? <RichTextDocument html={assessment.remarks} /> : null,
            },
            {
              label: 'Attachments',
              value:
                assessment.attachments.length > 0 ? (
                  <ul className="flex min-w-0 flex-col gap-1">
                    {assessment.attachments.map((file) => (
                      <li key={file.id} className="min-w-0">
                        <FileDownloadLink
                          tone="field"
                          fileName={file.fileName}
                          onDownload={() => onDownload(assessment, file)}
                        />
                      </li>
                    ))}
                  </ul>
                ) : null,
            },
            { label: 'Updated by', value: assessment.updatedByName },
            {
              label: 'Updated',
              value: assessment.updatedAt != null ? formatDateTime(assessment.updatedAt) : null,
              mono: true,
            },
          ]}
        />
      ) : (
        <Skeleton className="h-40 w-full" />
      )}
    </ProfileCard>
  );
}

/** Stage · Result · Weight · Score, and the total — the row's own figures, so the list cannot disagree. */
function SummaryCard({ row, weights }: { row: DeliberationApplicantRow; weights: ApplicantScoreWeights | null }) {
  const lines: SummaryLine[] = APPLICANT_ASSESSMENT_STAGES.map((s) => ({
    label: s.label,
    stage: row.stages.find((x) => x.stage === s.value),
    weight: weights?.weights.find((w) => w.stage === s.value)?.weight ?? null,
  }));
  const outOf = lines.reduce((sum, l) => sum + (l.weight ?? 0), 0);

  return (
    <ProfileCard id={`${ID}-profile-summary`} title="Summary" padded>
      <PreviewGrid<SummaryLine>
        rows={lines}
        rowKey={(l) => l.label}
        empty="No assessment stages."
        columns={[
          {
            header: 'Stage',
            width: 240,
            render: (l) => <TextCell value={l.label} />,
          },
          {
            header: 'Result',
            width: 140,
            render: (l) => <TextCell value={l.stage?.isHidden ? 'Hidden' : (l.stage?.resultLabel ?? null)} />,
            footer: () => <span className="text-slate-600">Total</span>,
          },
          {
            header: 'Weight (%)',
            width: 120,
            align: 'right',
            render: (l) => <TextCell mono numeric value={l.weight != null ? formatPoints(l.weight) : null} />,
            footer: () => <span className="font-mono tabular-nums text-slate-600">{formatPoints(outOf)}</span>,
          },
          {
            header: 'Score',
            width: 120,
            align: 'right',
            // "Hidden" is a word, not a figure — it takes the body face.
            render: (l) =>
              l.stage?.isHidden ? (
                <TextCell value="Hidden" />
              ) : (
                <TextCell mono numeric value={l.stage?.score != null ? formatPoints(l.stage.score) : null} />
              ),
            footer: () =>
              row.isTotalHidden ? (
                <span className="text-slate-600">Hidden</span>
              ) : row.total != null ? (
                <span className="font-mono tabular-nums text-slate-600">{formatPoints(row.total)}</span>
              ) : (
                // Score Points unset — nothing is scored, which is not a total of zero.
                <span className="text-slate-400">—</span>
              ),
          },
        ]}
      />
    </ProfileCard>
  );
}

/** Selected is the good outcome; the two refusals are closed; every stage between is in progress. */
function stageTone(applicant: ApplicantListItem): StatusTone {
  if (applicant.status === APPLICANT_STATUS.Selected) return 'active';
  if (applicant.status === APPLICANT_STATUS.NotQualified || applicant.status === APPLICANT_STATUS.NotSelected)
    return 'inactive';
  return 'pending';
}

function bannerTone(applicant: ApplicantListItem): 'emerald' | 'slate' | 'blue' {
  const tone = stageTone(applicant);
  return tone === 'active' ? 'emerald' : tone === 'inactive' ? 'slate' : 'blue';
}

/** The stage, and the total — or why there is none to state. */
function bannerMessage(row: DeliberationApplicantRow): string {
  const stage = `Application status: ${row.applicant.statusLabel}.`;

  if (row.isTotalHidden)
    return `${stage} The total score is hidden — it includes the Neuro-Psychiatric and Medical stages.`;
  if (row.total == null) return `${stage} Score points are not set, so no stage is scored yet.`;

  return `${stage} Total score: ${formatPoints(row.total)} of 100.`;
}

/** The Decision as saved — the three facts the form sets, and who last set them. */
function deliberationRows(deliberation: ApplicantDeliberation): PreviewRow[] {
  return [
    { label: 'Shortlisted', value: deliberation.isShortlisted ? 'Yes' : 'No' },
    { label: 'Note', value: deliberation.note ? <RichTextDocument html={deliberation.note} /> : null },
    { label: 'Updated by', value: deliberation.updatedByName },
    {
      label: 'Updated',
      value: deliberation.updatedAt != null ? formatDateTime(deliberation.updatedAt) : null,
      mono: true,
    },
  ];
}

/** The endorsement as recorded — the form's own order; the document downloads, never opens inline. */
function endorsementRows(deliberation: ApplicantDeliberation, onDownload: () => void): PreviewRow[] {
  return [
    { label: 'Endorsed by', value: deliberation.endorsedByName },
    {
      label: 'Endorsed at',
      // A calendar date — never the instant path.
      value: deliberation.endorsedDate != null ? formatDateOnly(deliberation.endorsedDate) : null,
      mono: true,
    },
    { label: 'Remarks', value: deliberation.endorsementRemarks },
    {
      label: 'Attachment',
      value: deliberation.endorsementFileName ? (
        <FileDownloadLink tone="field" fileName={deliberation.endorsementFileName} onDownload={onDownload} />
      ) : null,
    },
  ];
}

/** The Schedule of Oath Taking as recorded — the form's own order: place, then date and time. */
function oathRows(deliberation: ApplicantDeliberation): PreviewRow[] {
  return [
    { label: 'Place', value: deliberation.oathTakingPlace },
    {
      label: 'Date',
      // A calendar date — never the instant path.
      value: deliberation.oathTakingDate != null ? formatDateOnly(deliberation.oathTakingDate) : null,
      mono: true,
    },
    {
      label: 'Time',
      value: deliberation.oathTakingTime != null ? formatClockTime(deliberation.oathTakingTime) : null,
      mono: true,
    },
    {
      label: 'Notes',
      value: deliberation.oathTakingNotes != null ? <RichTextDocument html={deliberation.oathTakingNotes} /> : null,
    },
  ];
}

/** The planned Assignment as recorded — the form's own order. */
function assignmentRows(deliberation: ApplicantDeliberation): PreviewRow[] {
  return [
    { label: 'Unit', value: deliberation.assignmentOrgUnitName },
    {
      label: 'Effective date',
      value: deliberation.assignmentEffectiveDate != null ? formatDateOnly(deliberation.assignmentEffectiveDate) : null,
      mono: true,
    },
    { label: 'Remarks', value: deliberation.assignmentRemarks },
  ];
}

/** One fact per row; every row renders whatever the value, an absent one as an em dash. */
function applicantRows(applicant: ApplicantListItem, name: string): PreviewRow[] {
  return [
    { label: 'Name', value: name },
    { label: 'Email', value: applicant.email },
    { label: 'Mobile number', value: applicant.mobileNumber, mono: true },
    {
      label: 'Date applied',
      value: formatDateOnly(applicant.dateApplied),
      mono: true,
    },
    { label: 'Remarks', value: applicant.remarks },
  ];
}

function interviewRows(applicant: ApplicantListItem): PreviewRow[] {
  return [
    { label: 'Place', value: applicant.interviewPlace },
    {
      label: 'Date',
      value: applicant.interviewDate != null ? formatDateOnly(applicant.interviewDate) : null,
      mono: true,
    },
    {
      label: 'Time',
      value: applicant.interviewTime != null ? formatClockTime(applicant.interviewTime) : null,
      mono: true,
    },
  ];
}

function auditRows(applicant: ApplicantListItem): PreviewRow[] {
  return [
    { label: 'Updated by', value: applicant.updatedByName },
    {
      label: 'Updated',
      value: applicant.updatedAt != null ? formatDateTime(applicant.updatedAt) : null,
      mono: true,
    },
  ];
}
